import { Phone, PhoneMissed } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBlocksOn } from "@/lib/blocked-callers-api";
import { isDialable } from "@/lib/phone-number";
import {
  callBackPath, callBackTitle, clockTime, missedBadge, missedCallNumber, missedCallText,
  missedCallWho, newestMissed, railOffset, unseenMissed,
} from "@/lib/phone-page";
import { readMissedSeen, writeMissedSeen } from "@/lib/phone-work";
import { useColleagues, useMissedCalls } from "@/lib/queries";
import { accountScope } from "@/lib/session-cache";
import type { MissedCall } from "@/lib/types";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { canDialSecondLine, dialBlockedReason } from "@/softphone/call-control";
import { BlockMissedCallerButton, missedCallBlockTarget } from "@/softphone/block-caller";
import { ContactRowLink } from "@/softphone/contact-link";
import { useOutboundDial } from "@/softphone/manual-call";
import { RAIL_CELL, RAIL_LIST_ROW } from "@/softphone/rail";
import { useSoftphone } from "@/softphone/softphone-context";
import { PHONE_COMPANION } from "@/softphone/voicemail-suggestion";
import { seesCampaigns } from "@/lib/types";

/**
 * "Missed calls": the calls this phone did not take, newest first, each with a button to call the
 * other party back, one press away on every screen.
 *
 * It was half of the Phone page's stage between calls, beside the Dial box, where it could be
 * seen only from that page and only while nothing was going on. Now it is a standing button in
 * the phone's rail (`MissedCallsRailButton`), under the day's calls, and the list opens from it as
 * a popover the way the day's calls do. A narrow window has no rail, so there the Phone page still
 * draws the list (`MissedCallsList`) under its Dial box.
 *
 * A list behind a button says nothing until it is opened, and a call a rep missed is one they are
 * owed a look at. So the button counts the calls that came after the rep last had the list open
 * (`unseenMissed` in `lib/phone-page.ts`), which means this list, unlike the day's calls, is asked
 * for on every page: once a minute while the tab is in front, as the Phone page asked while it
 * was idle. How far the rep has read is this browser's (`readMissedSeen` in `lib/phone-work.ts`).
 *
 * It lives here and not in `routes/phone.tsx` because the rail is the dock's, and the dock cannot
 * import from a route.
 */

/** The rail's count and the list ask under one key, so the list opens on what the count read. */
const POLL = { refetchInterval: 60_000 } as const;

const NONE: readonly MissedCall[] = [];

/**
 * The list itself, for the rail's popover (`dense`) and for the Phone page of a narrow window.
 *
 * Read from `GET /phone/missed` (ADR-048 in `platform`), not from the call history: the history
 * is the legs this phone was on, and a call that never reached the phone (a caller on the rep's
 * own number while they were on a break or their phone was off) has no leg of theirs at all. The
 * server puts those beside the calls that rang, and says what became of each caller.
 *
 * A row is who called and when, over what happened. The sentence is not cut short: its end says
 * what became of the caller ("nobody took it"), which is the part a rep calls back on. A caller
 * the platform has a contact for is a link to it (`ContactRowLink`), over the whole row.
 *
 * Call back goes out the way the call came in (`callBackPath`, ADR-047): through the campaign
 * it was received on, placed by the dialer and brought back to this phone in the call's room,
 * never a plain call from the phone in its place. A rep not Ready on that campaign is told so
 * under the list, as is any refusal from the platform, where the Dial box would say it too.
 */
export function MissedCallsList({
  rows = 20, dense = false, onNavigate, onPlaced,
}: {
  /** How many of the newest are listed. The server sends twenty at most. */
  rows?: number;
  /** In the rail's popover: the popover's gutters, and no box of its own. */
  dense?: boolean;
  /** The rep followed a row to its contact: the popover around this closes itself. */
  onNavigate?: () => void;
  /** A call back went out, which the phone now shows: the same. */
  onPlaced?: () => void;
}) {
  const phone = useSoftphone();
  const colleagues = useColleagues();
  const out = useOutboundDial();
  const list = useMissedCalls({}, POLL);
  const blocked = dialBlockedReason(phone.registration, canDialSecondLine(phone.calls));
  const [refusal, setRefusal] = React.useState<string | null>(null);
  // The row whose call is being placed, so only its button shows the wait.
  const [placing, setPlacing] = React.useState<string | null>(null);
  // A rep reads no campaign or line on a missed call (ACL-652).
  const asRep = !seesCampaigns(useAuth().user?.role);
  const shown = (list.data ?? NONE).slice(0, rows);
  // Whether the rows' callers are blocked now, asked once for the whole list.
  const blocks = useBlocksOn(shown.map(missedCallBlockTarget)).data;

  const callBack = async (call: MissedCall, number: string) => {
    setRefusal(null);
    const back = callBackPath(call, out.path(number), asRep);
    if (back.via === "blocked") {
      setRefusal(back.reason);
      return;
    }
    setPlacing(call.id);
    try {
      await out.place(number, back.via === "campaign" ? back.campaign.campaign_id : null);
      onPlaced?.();
    } catch (e) {
      // A refusal from core-api has a sentence for the rep. A refusal from the phone itself has
      // already gone into the phone's banner, as it always has.
      if (e instanceof ApiError) setRefusal(e.message);
    } finally {
      setPlacing(null);
    }
  };

  const gutter = dense ? "px-4" : "px-3";
  if (list.isPending) {
    return <div className="flex justify-center py-6"><Spinner /></div>;
  }
  if (list.error) {
    return (
      <p className={cn("text-xs text-text-muted", dense ? "px-4 py-3" : "py-2")}>
        {list.error instanceof ApiError ? list.error.message : "Could not load your missed calls."}
      </p>
    );
  }
  if (shown.length === 0) {
    return (
      <p className={cn("text-xs text-text-muted", dense ? "px-4 py-3" : "py-2")}>
        Nothing missed. A call you did not take shows up here.
      </p>
    );
  }
  return (
    <div>
      <ul className={cn("divide-y divide-border", !dense && "rounded-md border border-border")}>
        {shown.map((call) => {
          const number = call.caller_number;
          const who = missedCallWho(call, colleagues.data, formatPhoneNumber);
          const shownNumber = missedCallNumber(call, colleagues.data, formatPhoneNumber);
          const back = number ? callBackPath(call, out.path(number), asRep) : null;
          return (
            <li key={call.id} className={cn("flex items-center gap-2.5 py-2", gutter, RAIL_LIST_ROW)}>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm text-text">
                    <ContactRowLink leadId={call.lead_id} name={who} onNavigate={onNavigate} />
                    {shownNumber && (
                      // `relative` puts the number over the row's link, so a rep can select and copy it.
                      <span className="relative ml-1.5 font-mono text-xs text-text-muted">{shownNumber}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs tabular text-text-subtle" title={call.at}>
                    {clockTime(call.at)}
                  </span>
                </div>
                <p className="text-xs text-text-muted">{missedCallText(call, asRep)}</p>
              </div>
              <BlockMissedCallerButton call={call} blocks={blocks} />
              {number && back && isDialable(number) ? (
                <Button
                  variant="outline"
                  size="icon-sm"
                  aria-label={`Call ${who} back`}
                  title={blocked ?? callBackTitle(back, asRep)}
                  disabled={blocked !== null || (placing !== null && placing !== call.id)}
                  loading={placing === call.id}
                  // Over the row's link to the contact, which is stretched across the row.
                  className="relative"
                  onClick={() => void callBack(call, number)}
                >
                  <Phone />
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
      {refusal && (
        <p className={cn("text-xs text-danger-11", dense ? "border-t border-border px-4 py-2" : "mt-2")} role="alert">
          {refusal}
        </p>
      )}
    </div>
  );
}

/** The popover's box. A little wider than the day's calls: a missed call's row is a sentence. */
const CONTENT = "flex w-96 flex-col p-0 max-h-[var(--radix-popover-content-available-height)]";

/**
 * The list in the phone's rail: a standing button under the day's calls, on every page.
 *
 * Built as `MyCallsRailButton` is, and for its reasons: a popover with its own root, because a
 * trigger without one would open and close the phone, whose root the rail is drawn inside; the
 * companion mark on its cell and its popover, so a press on either does not close the phone; out
 * beside whatever else the rail has open (`railOffset`), never over a call; opened only by the
 * rep's press, and out of the way by itself when a call rings or a voicemail is suggested. Pressing
 * the day's calls closes this and the other way round, each being a click outside the other.
 *
 * Its own part is the count: the calls missed since the rep last had the list open, over the icon
 * and in the button's name. Opening the list is reading it, so the count goes while it is open and
 * the newest call on it is remembered as read (`writeMissedSeen`). It closes on a row followed to
 * its contact and on a call back placed: both are the rep moving on to something the list is not.
 */
export function MissedCallsRailButton({
  phoneOpen, suggestionOpen, ringing,
}: {
  /** The phone's own popover is open. */
  phoneOpen: boolean;
  /** A voicemail is suggested and its choices are open (`VoicemailRailButton`). */
  suggestionOpen: boolean;
  /** A call is ringing this phone. */
  ringing: boolean;
}) {
  const account = accountScope(useAuth().user ?? null);
  const [open, setOpen] = React.useState(false);
  const [tipOpen, setTipOpen] = React.useState(false);
  React.useEffect(() => {
    if (ringing || suggestionOpen) setOpen(false);
  }, [ringing, suggestionOpen]);

  const calls = useMissedCalls({}, POLL).data ?? NONE;
  const [seen, setSeen] = React.useState(() => readMissedSeen(account));
  React.useEffect(() => {
    setSeen(readMissedSeen(account));
  }, [account]);
  // The mark only ever moves on: a list that lost its newest call to the week's window is not a
  // reason to count older ones again.
  const newest = newestMissed(calls);
  React.useEffect(() => {
    if (!open || newest === null) return;
    if (seen !== null && Date.parse(newest) <= Date.parse(seen)) return;
    setSeen(newest);
    writeMissedSeen(account, newest);
  }, [open, newest, seen, account]);
  const unseen = open ? 0 : unseenMissed(calls, seen);
  const badge = missedBadge(unseen);
  const name = unseen > 0 ? `Missed calls, ${unseen} new` : "Missed calls";

  const companion = { [PHONE_COMPANION]: "" };
  const close = () => setOpen(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={RAIL_CELL} {...companion}>
          <Tooltip content={name} side="left" open={tipOpen && !open} onOpenChange={setTipOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                // Radix opens the tooltip from a focus handler that stands down for a prevented event.
                onFocus={(e) => e.preventDefault()}
                aria-label={name}
                className={cn(
                  "relative flex size-9 items-center justify-center rounded-full transition-colors",
                  open
                    ? "bg-frame-hover text-text"
                    : "text-text-muted hover:bg-frame-hover hover:text-text",
                )}
              >
                <PhoneMissed className="size-[18px]" aria-hidden />
                {badge && (
                  // On the button's corner, clear of the glyph: its cross is what tells this
                  // icon from the phone's own, two rows up.
                  <span
                    className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-9 px-1 text-2xs font-semibold leading-none text-white ring-2 ring-frame tabular"
                    aria-hidden
                  >
                    {badge}
                  </span>
                )}
              </button>
            </PopoverTrigger>
          </Tooltip>
        </div>
      </PopoverAnchor>
      <PopoverContent
        side="left"
        align="start"
        sideOffset={railOffset({ phoneOpen, suggestionOpen })}
        aria-label="Missed calls"
        className={CONTENT}
        {...companion}
      >
        <div className="shrink-0 border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-text">Missed calls</h2>
          <p className="text-xs text-text-muted">The last seven days, newest first.</p>
        </div>
        <div className="min-h-0 overflow-y-auto scrollbar-subtle">
          <MissedCallsList dense onNavigate={close} onPlaced={close} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
